import {
	CSSProperties,
	ReactElement,
	useMemo,
	useState
} from "react";
import { CalendarLayout } from "../CalendarLayout/CalendarLayout";
import { newGuid, Guid } from "client_legacy/ndaw/guid";
import {
	Button,
	IconValue,
	Typography
} from "client/Components";
import styles from "./Header.module.less";
import classNames from "classnames";
import {
	HeaderEventToRender,
	getMoreEventsCountForADay,
	getEventsForDateRange,
	DEFAULT_CALENDAR_HEADER_ROW_COUNT,
	RegularHeaderEventToRender
} from "./getEventsForDayRange";
import { EventCard, EventCardColor } from "../EventCard/EventCard";
import {
	generateTrackingId,
	isNotNil,
	setTrackingId
} from "client/Misc/utils";
import { localize } from "client_legacy/ndaw/culture";
import {
	CalendarAppointment,
	CalendarDateRange,
	EditAppointmentInfo
} from "../../CalendarPage";
import { DateOnly } from "client/DateAndTime/dateOnly";
import { TimeZone } from "client/DateAndTime/timezones";
import { convertToDateTime, getDayOfWeek } from "client/DateAndTime/dateUtils";
import { getIsToday } from "client/DateAndTime/dateTimeUtils";
import { formatDayOfWeek } from "client/DateAndTime/culture";
import { CalendarColor } from "client/Api/Calendar/calendar";
import { useAppointmentSpan } from "../../AppointmentSpan";
import { PlaceholderEventCard } from "../EventCard/PlaceholderEventCard";
import { Coordinate } from "client/Misc/commonTypes";
import { TimeOnly } from "client/DateAndTime/timeOnly";
import { formatCalendarLocations } from "../../calendarUtils";
import { getClientId } from "client/Misc/clientId";
import { AppointmentOrIdentifier } from "../../common";

export type HeaderProps = {
	appointments: readonly CalendarAppointment[];
	dateRange: CalendarDateRange;
	timezone: TimeZone;
	today: Date;
	onOpenDetailsDialog(appointmentOrIdentifier: AppointmentOrIdentifier): void;
	onCreate: (
		coordinate: Coordinate,
		date: DateOnly,
		time?: TimeOnly
	) => void;
	onEdit: (
		coordinate: Coordinate,
		data: EditAppointmentInfo
	) => void;
	onAfterMutatingAppointment: (calendarIds: readonly string[]) => void;
	editingAppointmentId: string | undefined;
};

export const Header = (props: HeaderProps): ReactElement => {
	const {
		appointments,
		dateRange,
		timezone,
		today,
		onOpenDetailsDialog,
		onCreate,
		onEdit,
		onAfterMutatingAppointment,
		editingAppointmentId
	} = props;

	const [isExpanded, setIsExpanded] = useState<boolean>(false);

	const events = useMemo(
		(): readonly HeaderEventToRender[] => getEventsForDateRange(
			appointments,
			dateRange.toRender,
			timezone
		),
		[
			appointments,
			dateRange.toRender,
			timezone
		]
	);

	const eventsToRender = useMemo(
		(): readonly HeaderEventToRender[] => !isExpanded
			? events.filter(x => x.position.row <= DEFAULT_CALENDAR_HEADER_ROW_COUNT - 1)
			: events,
		[
			events,
			isExpanded
		]
	);

	const daysToRender = useMemo(
		(): readonly DayToRender[] => {
			return dateRange.toRender
				.map(
					date => {
						const dateTime: Date = convertToDateTime(date, timezone);

						return ({
							uniqueId: newGuid(),
							isToday: getIsToday(
								dateTime,
								timezone,
								today
							),
							dayOfTheWeek: formatDayOfWeek(getDayOfWeek(date), "short"),
							dayOfTheMonth: date.day,
							moreEventsCount: getMoreEventsCountForADay(
								events,
								dateTime,
								timezone
							),
							date
						});
					}
				);
		},
		[
			dateRange.toRender,
			events,
			timezone,
			today
		]
	);

	const renderMoreButtons = (): ReactElement | null => {
		return (
			<CalendarLayout trackingId="events-more-buttons">
				{daysToRender.map(x => {
					return (
						<CalendarLayout.Column
							trackingId={generateTrackingId("day-column", `|${x.date.toISOString()}`)}
							key={x.uniqueId}
							isToday={x.isToday}
							onClick={
								(coordinate: Coordinate): void =>
									onCreate(coordinate, x.date)
							}
						>
							{x.moreEventsCount > 0 && (
								<Button
									kind="transparent-for-list"
									text={isExpanded
										? localize("_ViewLess")
										: {
											template: localize("_XMore"),
											replacements: [
												{
													what: "{{Number}}",
													filler: String(x.moreEventsCount)
												}
											]
										}
									}
									icon={isExpanded
										? "chevron-up"
										: "chevron-down"
									}
									onClick={() => setIsExpanded(status => !status)}
									bubbleClick={false}
								/>
							)}
						</CalendarLayout.Column>
					);
				})}
			</CalendarLayout>
		);
	};

	const eventsHeight = useMemo(
		(): number => {
			const rowCount: number = Math.max(
				...eventsToRender.map(x => x.position.row),
				0
			) + 1;

			return rowCount * CALENDAR_HEADER_ITEM_HEIGHT;
		},
		[
			eventsToRender
		]
	);

	return (
		<div
			{...setTrackingId("calendar-header")}
			className={styles.root}
		>
			<div className={styles.dayTitles}>
				<CalendarLayout trackingId="days">
					{daysToRender.map(x => (
						<CalendarLayout.Column
							trackingId={generateTrackingId("day-column", `|${x.date.toISOString()}`)}
							key={x.uniqueId}
							isToday={x.isToday}
						>
							<div
								className={classNames(
									styles.dayInfo,
									{
										[styles.dayInfo_today]: x.isToday
									}
								)}
							>
								<Typography
									textColor={!x.isToday
										? "secondary"
										: undefined
									}
								>
									{x.dayOfTheWeek}
								</Typography>
								<h2>{x.dayOfTheMonth}</h2>
							</div>
						</CalendarLayout.Column>
					))}
				</CalendarLayout>
			</div>
			{(eventsToRender.length > 0) && (
				<div
					className={styles.events}
				>
					<div
						className={styles.grid}
						style={{
							height: eventsHeight
						}}
					>
						<div className={styles.eventsColumns}>
							<CalendarLayout trackingId="events">
								{daysToRender.map(x => (
									<CalendarLayout.Column
										trackingId={generateTrackingId("event-column", `|${x.date.toISOString()}`)}
										key={x.uniqueId}
										isToday={x.isToday}
										onClick={
											(coordinate: Coordinate): void =>
												onCreate(coordinate, x.date)
										}
									/>
								))}
							</CalendarLayout>
						</div>
						<div className={styles.eventsContainer}>
							{eventsToRender.map((x): ReactElement => (
								<Event
									key={getClientId(x)}
									event={x}
									timezone={timezone}
									onOpenDetailsDialog={onOpenDetailsDialog}
									onEdit={onEdit}
									onAfterMutatingAppointment={onAfterMutatingAppointment}
									editingAppointmentId={editingAppointmentId}
								/>
							))}
						</div>
					</div>
				</div>
			)}
			<div className={styles.moreButtons}>
				{renderMoreButtons()}
			</div>
		</div>
	);
};
Header.displayName = "Header";

type EventProps = {
	event: HeaderEventToRender;
	timezone: TimeZone;
	onOpenDetailsDialog(appointmentOrIdentifier: AppointmentOrIdentifier): void;
	onEdit: (
		coordinate: Coordinate,
		data: EditAppointmentInfo
	) => void;
	onAfterMutatingAppointment: (calendarIds: readonly string[]) => void;
	editingAppointmentId: string | undefined;
};

const Event = (props: EventProps): ReactElement => {
	const {
		event,
		timezone,
		onOpenDetailsDialog,
		onEdit,
		onAfterMutatingAppointment,
		editingAppointmentId
	} = props;

	const clientId: Guid = getClientId(event);

	const {
		left,
		right,
		row
	} = event.position;

	const style: CSSProperties = {
		inset: `${row * CALENDAR_HEADER_ITEM_HEIGHT}px ${right}% auto ${left}%`
	};

	return (
		<div
			className={styles.eventCard}
			style={style}
		>
			{event.kind === "placeholder"
				? (
					<PlaceholderEventCard
						trackingId={generateTrackingId("placeholder-event", `|${clientId}`)}
						color={isNotNil(event.color) ? calendarColorToEventCardColorMap[event.color] : undefined}
					/>
				) : (
					<RegularEventCard
						event={event}
						onOpenDetailsDialog={onOpenDetailsDialog}
						timezone={timezone}
						onEdit={onEdit}
						onAfterMutatingAppointment={onAfterMutatingAppointment}
						editingAppointmentId={editingAppointmentId}
						trackingId={generateTrackingId("event", `|${clientId}`)}
					/>
				)
			}
		</div>
	);
};

Event.displayName = "Event";

type RegularEventCardProps = {
	event: RegularHeaderEventToRender;
	timezone: TimeZone;
	onOpenDetailsDialog: (appointmentOrIdentifier: AppointmentOrIdentifier) => void;
	onEdit: (
		coordinate: Coordinate,
		data: EditAppointmentInfo
	) => void;
	onAfterMutatingAppointment: (calendarIds: readonly string[]) => void;
	editingAppointmentId: string | undefined;
	trackingId: string;
};

const RegularEventCard = (props: RegularEventCardProps): ReactElement => {
	const {
		event,
		timezone,
		onOpenDetailsDialog,
		onEdit,
		onAfterMutatingAppointment,
		editingAppointmentId,
		trackingId
	} = props;

	const dateSpan: string = useAppointmentSpan(event, timezone);

	const {
		id,
		subject,
		start,
		end,
		isAllDayEvent,
		isRecurring,
		hasAttachments,
		isPrivate,
		isCancelled,
		color,
		locations,
		myResponseStatus,
		isOrganizer,
		position,
		canEdit,
		calendarId
	} = event;

	const appointmentOrIdentifier = useMemo(
		(): AppointmentOrIdentifier => {
			if (isNotNil(id)) {
				return {
					id,
					calendarId
				};
			}

			return ({
				kind: "basic",
				subject,
				start,
				end,
				isAllDayEvent
			});
		},
		[
			end,
			id,
			isAllDayEvent,
			start,
			subject,
			calendarId
		]
	);

	const icons: IconValue[] = [];

	if (isPrivate) {
		icons.push("lock");
	}

	if (isRecurring) {
		icons.push("arrows-rotate");
	}

	if (hasAttachments) {
		icons.push("paperclip");
	}

	const tooltip: string = `${subject} \n${dateSpan} \n${formatCalendarLocations(locations) ?? ""}`;

	const canRespond: boolean = !isCancelled
		&& !isOrganizer
		&& canEdit
		&& (myResponseStatus?.isResponseOutstanding ?? false);

	return (
		<EventCard
			trackingId={trackingId}
			color={isNotNil(color) ? calendarColorToEventCardColorMap[color] : undefined}
			appointmentOrIdentifier={appointmentOrIdentifier}
			tooltip={tooltip}
			timezone={timezone}
			variant="small"
			onOpenDetailsDialog={() => onOpenDetailsDialog(appointmentOrIdentifier)}
			hasDashedBorder={canRespond}
			onEdit={onEdit}
			onAfterMutatingAppointment={onAfterMutatingAppointment}
			editingAppointmentId={editingAppointmentId}
		>
			{!position.isStartDateVisible && (
				<EventCard.PreSuffix
					kind="prefix"
					date={start}
					timezone={timezone}
				/>
			)}

			<EventCard.Info
				subject={subject}
				isCanceled={isCancelled}
				cardVisualHeightInMinutes={30}
			/>
			<EventCard.Icons
				icons={icons}
			/>

			{!position.isEndDateVisible && (
				<EventCard.PreSuffix
					kind="suffix"
					date={end}
					timezone={timezone}
				/>
			)}
		</EventCard>
	);
};
RegularEventCard.displayName = "RegularEventCard";

type DayToRender = {
	uniqueId: Guid;
	isToday: boolean;
	dayOfTheWeek: string;
	dayOfTheMonth: number;
	moreEventsCount: number;
	date: DateOnly;
};

const CALENDAR_HEADER_ITEM_HEIGHT = 34;

export const calendarColorToEventCardColorMap: { [key in CalendarColor]: EventCardColor } = {
	[CalendarColor.Warning]: "warning",
	[CalendarColor.Success]: "success",
	[CalendarColor.Info]: "info",
	[CalendarColor.Error]: "danger",
	[CalendarColor.Color1]: "calendar-1",
	[CalendarColor.Color2]: "calendar-2",
	[CalendarColor.Color3]: "calendar-3",
	[CalendarColor.Color4]: "calendar-4",
	[CalendarColor.Color5]: "calendar-5",
	[CalendarColor.Color6]: "calendar-6",
	[CalendarColor.Color7]: "calendar-7",
	[CalendarColor.Color8]: "calendar-8",
	[CalendarColor.Color9]: "calendar-9",
	[CalendarColor.Color10]: "calendar-10",
	[CalendarColor.Color11]: "calendar-11",
	[CalendarColor.Color12]: "calendar-12",
	[CalendarColor.Color13]: "calendar-13",
	[CalendarColor.Color14]: "calendar-14",
	[CalendarColor.Color15]: "calendar-15",
	[CalendarColor.Color16]: "calendar-16"
};
